import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Calendar, Zap } from 'lucide-react';
import PublicNavbar from '../../components/PublicNavbar';
import Footer from '../../components/Footer';

export default function Pricing() {
  const { t } = useTranslation();

  const pricingFeatures = [
    t('pricing.feat1', 'Unlimited rotas & shifts'),
    t('pricing.feat2', 'AI auto-fill staff suggestions'),
    t('pricing.feat3', 'Time clock with QR scanning'),
    t('pricing.feat4', 'Payroll export (12 formats)'),
    t('pricing.feat5', 'Analytics dashboard (4 tabs)'),
    t('pricing.feat6', 'Shift swap management'),
    t('pricing.feat7', 'Groups & locations'),
    t('pricing.feat8', 'Staff accreditations & expiry tracking'),
    t('pricing.feat9', 'Multi-week & multi-month views'),
    t('pricing.feat10', 'WTD compliance checks'),
    t('pricing.feat11', 'Staff availability & profiles'),
    t('pricing.feat12', 'Role-based access control'),
    t('pricing.feat13', 'Multi-language support (10 languages)'),
    t('pricing.feat14', 'Email & in-app notifications'),
    t('pricing.feat15', 'Coverage gap forecasting'),
    t('pricing.feat16', '14-day free trial · Cancel anytime'),
    t('pricing.feat17', 'Staff self-service dashboard'),
    t('pricing.feat18', 'Shift swap board & marketplace'),
    t('pricing.feat19', 'Reports & CSV/print export'),
    t('pricing.feat20', 'Full audit log & activity trail'),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />
      <div className="max-w-5xl mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('pricing.title', 'Simple, transparent pricing')}
          </h1>
          <p className="text-xl text-gray-600">{t('pricing.subtitle', 'One plan. All features. No surprises.')}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {/* Standalone */}
          <div className="bg-white rounded-2xl shadow-xl border-2 border-primary p-8 relative">
            <div className="absolute -top-3 left-6">
              <span className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">{t('pricing.popular', 'POPULAR')}</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <Calendar size={24} className="text-primary" />
              <h3 className="text-xl font-bold text-gray-900">{t('pricing.standalone', 'Standalone')}</h3>
            </div>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="text-5xl font-bold text-gray-900">£5</span>
              <span className="text-gray-500">/{t('pricing.month', 'month')}</span>
            </div>
            <p className="text-sm text-gray-500 mb-6">{t('pricing.perBusiness', 'per user per month')}</p>
            <ul className="space-y-2.5 mb-8">
              {pricingFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle size={15} className="text-green-500 mt-0.5 flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Link
              to="/register"
              className="block w-full bg-primary text-white text-center py-3 rounded-xl font-semibold hover:bg-primary-dark transition-colors"
            >
              {t('publicNav.startTrial', 'Start Free Trial')}
            </Link>
          </div>

          {/* Bundle */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-xl border-2 border-secondary p-8 relative">
            <div className="absolute -top-3 left-6">
              <span className="bg-secondary text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                <Zap size={10} /> {t('pricing.bestValue', 'BEST VALUE')}
              </span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <Calendar size={24} className="text-secondary" />
              <h3 className="text-xl font-bold text-gray-900">{t('pricing.bundle', 'Bundle')}</h3>
            </div>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="text-5xl font-bold text-gray-900">£2.50</span>
              <span className="text-gray-500">/{t('pricing.month', 'month')}</span>
            </div>
            <p className="text-sm text-gray-500 mb-1">{t('pricing.perBusiness', 'per user per month')}</p>
            <p className="text-sm text-green-600 font-medium mb-6">
              {t('pricing.bundleSave', 'Save 50% with BuildMyChecks')}
            </p>
            <ul className="space-y-2.5 mb-8">
              {pricingFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle size={15} className="text-green-500 mt-0.5 flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Link
              to="/register"
              className="block w-full bg-secondary text-white text-center py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              {t('publicNav.startTrial', 'Start Free Trial')}
            </Link>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-500">
            {t('pricing.vatNote', 'All prices in GBP. VAT may apply. Need a custom plan?')}{' '}
            <Link to="/contact" className="text-primary font-medium hover:underline">{t('pricing.contactUs', 'Contact us')}</Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
